import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Layout
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// Public Pages
import HomePage from './components/pages/HomePage';
import DatasetPage from './components/pages/DatasetPage';
import DatasetDetailPage from './components/pages/DatasetDetailPage';
import AnalitikPage from './components/pages/AnalitikPage';
import BeritaPage from './components/pages/BeritaPage';
import BeritaDetailPage from './components/pages/BeritaDetailPage';
import ProfilPage from './components/pages/ProfilPage';
import PetaPage from './components/pages/PetaPage';

// Auth
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';

// Dashboard
import DashboardLayout from './components/dashboard/DashboardLayout';
import DashboardOverview from './components/dashboard/DashboardOverview';
import DashboardDatasets from './components/dashboard/DashboardDatasets';
import DashboardBerita from './components/dashboard/DashboardBerita';
import DashboardUsers from './components/dashboard/DashboardUsers';
import DashboardOrganisasi from './components/dashboard/DashboardOrganisasi';
import DashboardActivity from './components/dashboard/DashboardActivity';
import DashboardKategori from './components/dashboard/DashboardKategori';
import DashboardProfil from './components/dashboard/DashboardProfil';
import DashboardAnalitik from './components/dashboard/DashboardAnalitik';
import DashboardAI from './components/dashboard/DashboardAI';

// Common
import ProtectedRoute from './components/common/ProtectedRoute';
import NgadhuAI from './components/common/NgadhuAI';
import Global3DBackground from './components/effects/Global3DBackground';

const AppContent = () => {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');
  const isAuth = ['/login', '/register'].includes(location.pathname);

  return (
    <div style={{ position: 'relative', overflowX: 'hidden' }}>
      <Global3DBackground />
      {!isDashboard && !isAuth && <Navbar />}
      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        >
          <Routes location={location} key={location.pathname}>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/dataset" element={<DatasetPage />} />
        <Route path="/dataset/:id" element={<DatasetDetailPage />} />
        <Route path="/analitik" element={<AnalitikPage />} />
        <Route path="/peta" element={<PetaPage />} />
        <Route path="/berita" element={<BeritaPage />} />
        <Route path="/berita/:id" element={<BeritaDetailPage />} />
        <Route path="/profil" element={<ProfilPage />} />

        {/* Auth Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* Dashboard Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }>
          <Route index element={<DashboardOverview />} />
          <Route path="datasets" element={
            <ProtectedRoute roles={['admin', 'opd']}>
              <DashboardDatasets />
            </ProtectedRoute>
          } />
          <Route path="berita" element={
            <ProtectedRoute roles={['admin', 'opd']}>
              <DashboardBerita />
            </ProtectedRoute>
          } />
          <Route path="analitik" element={<DashboardAnalitik />} />
          <Route path="ai" element={<DashboardAI />} />
          <Route path="users" element={
            <ProtectedRoute roles={['admin']}>
              <DashboardUsers />
            </ProtectedRoute>
          } />
          <Route path="organisasi" element={
            <ProtectedRoute roles={['admin']}>
              <DashboardOrganisasi />
            </ProtectedRoute>
          } />
          <Route path="kategori" element={
            <ProtectedRoute roles={['admin']}>
              <DashboardKategori />
            </ProtectedRoute>
          } />
          <Route path="activity" element={
            <ProtectedRoute roles={['admin']}>
              <DashboardActivity />
            </ProtectedRoute>
          } />
          <Route path="profil" element={<DashboardProfil />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={
          <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', paddingTop: 72 }}>
            <h1 style={{ fontSize: '4rem', color: 'var(--primary-300)' }}>404</h1>
            <p style={{ color: 'var(--gray-500)', marginBottom: '1rem' }}>Halaman tidak ditemukan</p>
            <a href="/" className="btn btn-primary">Kembali ke Beranda</a>
          </div>
        } />
          </Routes>
        </motion.div>
      </AnimatePresence>
      {!isDashboard && !isAuth && <Footer />}
    </div>
  );
};

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <Toaster position="top-right" toastOptions={{
            duration: 3000,
            style: { fontFamily: 'Plus Jakarta Sans', fontSize: '0.9rem', borderRadius: '10px', padding: '12px 16px' },
            success: { iconTheme: { primary: '#32966c', secondary: '#fff' } },
            error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } }
          }} />
          <NgadhuAI />
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
